/**
 * Echo User Context and Provider
 * Manages Echo user state, role, and auto-registration
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth, useUser, useAccessToken } from '../auth/hooks';
import { useDevModeConfig } from './useDevModeConfig';
import type { EchoUser, EchoUserContextValue } from '../api/types/echo';

/**
 * Check if we're in a deployed environment (vs local development).
 * Deployed environments have VITE_AUTHORITY set (Cognito auth).
 */
function isDeployedEnvironment(): boolean {
  return !!import.meta.env.VITE_AUTHORITY;
}

/**
 * Ensure URL uses HTTPS for deployed environments.
 * Forces HTTPS for any non-localhost URL.
 */
function ensureHttps(url: string): string {
  if (!url) return url;
  // Force HTTPS for non-localhost URLs
  if (!url.includes('localhost') && !url.includes('127.0.0.1')) {
    // Handle both http:// and missing protocol
    if (url.startsWith('http://')) {
      return url.replace('http://', 'https://');
    }
    // If no protocol, add https://
    if (!url.startsWith('https://')) {
      return 'https://' + url.replace(/^\/\//, '');
    }
  }
  return url;
}

/** Cached dev API token */
let cachedDevToken: string | null = null;
let cachedDevTokenExpiry: number = 0;

/**
 * Get dev API token using /auth/token endpoint.
 * Matches the pattern from iris.py/fastapi.py.
 * Only used for LOCAL development, not deployed environments.
 */
async function getDevApiToken(): Promise<string | null> {
  // Never use dev API token in deployed environments
  if (isDeployedEnvironment()) {
    return null;
  }

  // Return cached token if valid
  if (cachedDevToken && Date.now() < cachedDevTokenExpiry - 60000) {
    return cachedDevToken;
  }

  const user = import.meta.env.VITE_DEV_API_USER;
  const password = import.meta.env.VITE_DEV_API_PASSWORD;
  const apiUrl = import.meta.env.VITE_DEV_API_URL;

  if (!user || !password || !apiUrl) {
    return null;
  }

  try {
    const basicAuth = btoa(`${user}:${password}`);
    const response = await fetch(`${apiUrl}/auth/token`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${basicAuth}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        username: user,
        password: password,
      }),
    });

    if (!response.ok) {
      console.error('[EchoUser] Token request failed:', response.status);
      return null;
    }

    const data = await response.json();
    cachedDevToken = data.access_token;
    cachedDevTokenExpiry = Date.now() + (data.expires_in || 1800) * 1000;
    return cachedDevToken;
  } catch (error) {
    console.error('[EchoUser] Error getting token:', error);
    return null;
  }
}

/**
 * Get the appropriate Authorization header based on environment.
 * In deployed environments, always use OIDC token.
 * In local dev with 'dev' target, use dev API credentials.
 */
async function getAuthHeader(apiTarget: string, token: string | null): Promise<string> {
  // In deployed environments, always use OIDC token
  if (isDeployedEnvironment()) {
    return token ? `Bearer ${token}` : '';
  }

  // Local dev with 'dev' target - try dev API credentials
  if (apiTarget === 'dev') {
    const devToken = await getDevApiToken();
    if (devToken) {
      return `Bearer ${devToken}`;
    }
  }
  return token ? `Bearer ${token}` : '';
}

const EchoUserContext = createContext<EchoUserContextValue | null>(null);

export interface EchoUserProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that manages Echo user state
 * Automatically fetches or creates Echo user when authenticated
 */
export function EchoUserProvider({ children }: EchoUserProviderProps) {
  const auth = useAuth();
  const oidcUser = useUser();
  const token = useAccessToken();
  const { getEffectiveApiUrl, config: devModeConfig } = useDevModeConfig();

  const [user, setUser] = useState<EchoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Track if we've already attempted to fetch to prevent loops
  const hasFetchedRef = useRef(false);
  const lastSubRef = useRef<string | null>(null);

  const fetchUser = useCallback(async () => {
    // If not authenticated, clear user and stop loading
    if (!auth.isAuthenticated || !oidcUser) {
      setUser(null);
      setIsLoading(false);
      hasFetchedRef.current = false;
      lastSubRef.current = null;
      return;
    }

    const cognitoSub = oidcUser.sub;
    const email = oidcUser.email || '';

    if (!cognitoSub) {
      setError(new Error('No Cognito sub found in user profile'));
      setIsLoading(false);
      return;
    }

    // Prevent re-fetching for the same user
    if (hasFetchedRef.current && lastSubRef.current === cognitoSub) {
      return;
    }

    hasFetchedRef.current = true;
    lastSubRef.current = cognitoSub;
    setIsLoading(true);
    setError(null);

    // Get the effective API URL (respects dev mode gear icon setting)
    // Ensure HTTPS for deployed environments
    const rawUrl = getEffectiveApiUrl();
    const apiUrl = ensureHttps(rawUrl);
    console.log('[EchoUser] URL debug:', { rawUrl, apiUrl, VITE_API_URL: import.meta.env.VITE_API_URL });
    const authHeader = await getAuthHeader(devModeConfig.apiTarget, token);

    // Create an AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      // Try to get existing user first
      const getResponse = await fetch(`${apiUrl}/echo-users/by-sub/${cognitoSub}`, {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      if (getResponse.ok) {
        const echoUser = await getResponse.json();
        setUser(echoUser);
        setIsLoading(false);
        clearTimeout(timeoutId);
        return;
      }

      // If 404, try to create user
      if (getResponse.status === 404) {
        const createResponse = await fetch(`${apiUrl}/echo-users/`, {
          method: 'POST',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ cognito_sub: cognitoSub, email }),
          signal: controller.signal,
        });

        if (createResponse.ok) {
          const echoUser = await createResponse.json();
          setUser(echoUser);
          setIsLoading(false);
          clearTimeout(timeoutId);
          return;
        }
      }

      // API returned an error - create a mock user so the app can function
      // This allows the frontend to work while backend is being developed
      console.warn(`Echo API not available (${getResponse.status}), using fallback user`);
      setUser({
        id: 0,
        cognito_sub: cognitoSub,
        email: email,
        role: 'admin', // Default to admin for development
        created_at: new Date().toISOString(),
      });
    } catch (err) {
      clearTimeout(timeoutId);

      // On any error, create a fallback user so the app can still function
      console.warn('Echo API unavailable, using fallback user:', err);
      setUser({
        id: 0,
        cognito_sub: cognitoSub,
        email: email,
        role: 'admin', // Default to admin for development
        created_at: new Date().toISOString(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, oidcUser?.sub, oidcUser?.email, token, getEffectiveApiUrl, devModeConfig.apiTarget]);

  // Fetch user when auth state changes - but only once per session
  useEffect(() => {
    fetchUser();
  }, [auth.isAuthenticated, oidcUser?.sub]);

  const isAdmin = useMemo(() => {
    return user?.role === 'admin';
  }, [user?.role]);

  const value = useMemo<EchoUserContextValue>(
    () => ({
      user,
      isLoading,
      isAdmin,
      error,
      refetch: fetchUser,
    }),
    [user, isLoading, isAdmin, error, fetchUser]
  );

  return (
    <EchoUserContext.Provider value={value}>
      {children}
    </EchoUserContext.Provider>
  );
}

/**
 * Hook to access Echo user context
 * Must be used within an EchoUserProvider
 */
export function useEchoUser(): EchoUserContextValue {
  const context = useContext(EchoUserContext);
  if (!context) {
    throw new Error('useEchoUser must be used within an EchoUserProvider');
  }
  return context;
}

/**
 * Hook to check if current user is admin
 */
export function useIsAdmin(): boolean {
  const { isAdmin } = useEchoUser();
  return isAdmin;
}

/**
 * Hook to get current user email
 */
export function useUserEmail(): string | null {
  const { user } = useEchoUser();
  return user?.email ?? null;
}
